/**
 * Strings for popups, card tab lines and association pages (batch E).
 * Grouped by feature:
 *  - tabLines.*      : card tab line lists (activities, companies, people, permissions, notifications)
 *  - associations.*  : labels shared by both association pages (edit + view) and the list cards
 *  - addPeople.*     : niup-add-people-popup
 *  - requestAuth.*   : niup-request-auth-popup
 *  - requestRepresentation.* : niup-request-representation-popup
 *  - associationEdit.* / associationView.* : page-specific strings
 */
export const popups = {
  // Shared association labels reused across the list cards and both association pages.
  associations: {
    // Permission/user types (NiupAssociationType enum).
    permissionTypes: {
      owner: 'Owner',
      admin: 'Administrator',
      adminShort: 'Admin',
      user: 'User',
    },
    // Association status labels (NiupAssociationStatus enum).
    statuses: {
      active: 'Active',
      pending: 'Pending',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      removed: 'Removed',
    },
    // Representative direction labels (RepresentativeStatus enum).
    representativeStatus: {
      representation: 'I represent',
      represented: 'Represent me',
      mutual: 'Mutual',
    },
    loadFailed: 'Failed to load association data.',
    deleteFailed: 'Failed to delete association.',
    savedSuccess: 'Changes saved successfully.',
    typeOfUserTitle: 'Type of user',
    rolesTitle: 'Roles',
    offlineDaysTitle: 'Offline days',
    daysLabel: 'days',
    removeTitle: 'Remove association?',
    removeMessage: 'This will permanently remove this association. This action cannot be undone.',
    removeAssociation: 'Remove association',
  },

  // Card tab line lists.
  tabLines: {
    // niup-card-tab-line-activities (+ helper).
    activities: {
      searchPlaceholder: 'Enter your search terms',
      resultsTitle: 'Results',
      resultsCount: '{count} Results',
      empty: 'No register found',
      unknownName: 'Unknown',
      // Activity type labels (ActivityType enum).
      types: {
        associationRequest: 'Association Request',
        authenticationRequest: 'Authentication Request',
        security: 'Security',
        shareGroup: 'Share Group',
        associations: 'Associations',
        miscellaneous: 'Miscellaneous',
        activity: 'Activity',
      },
    },
    // niup-card-tab-line-companies.
    companies: {
      title: 'Companies',
      empty: 'No companies found.',
      aboutAria: 'About companies',
      aboutModalTitle: 'About companies',
      tabActive: 'Active',
    },
    // niup-card-tab-line-people.
    people: {
      title: 'People',
      empty: 'No people found.',
      aboutAria: 'About people',
      aboutModalTitle: 'About people',
      tabs: {
        representMe: 'Represent me',
        iRepresent: 'I represent',
      },
    },
    // niup-card-tab-line-permissions.
    permissions: {
      empty: 'No permissions found.',
    },
    // niup-card-tab-line-notifications.
    notifications: {
      empty: 'No notifications.',
      loadFailed: 'Failed to load notifications.',
      acceptFailed: 'Failed to accept request.',
      rejectFailed: 'Failed to reject request.',
      cancelFailed: 'Failed to cancel request.',
      reject: 'Reject',
      accept: 'Accept',
      seeActivity: 'See activity',
      tabs: {
        all: 'All',
        requiresAction: 'Requires action',
        security: 'Security',
      },
      // Relative time labels for notification timestamps.
      time: {
        justNow: 'Just now',
        minutesAgo: '{count}m ago',
        hoursAgo: '{count}h ago',
        daysAgo: '{count}d ago',
      },
    },
  },

  // niup-add-people-popup.
  addPeople: {
    title: 'Add people',
    description: 'Select people to add to this permission group.',
    searchPlaceholder: 'Enter your search terms',
    empty: 'No people found.',
    loadFailed: 'Failed to load people.',
  },

  // niup-request-auth-popup.
  requestAuth: {
    title: 'Request authentication',
    description: "Ask for someone you know to verify your account's authenticity. Send them a message by adding their email or Niup code.",
    contactLabel: 'Email or Niup code *',
    inputPlaceholder: 'Enter an email or a Niup code',
    hint: "By clicking Enter or the Tab it's possible to add multiple contacts",
    removeContact: 'Remove {name}',
    messageLabel: 'Custom message',
    defaultMessage: 'Hello!\nCould you please help me verify my account by approving this authentication request?',
    note: '* Required field. Email is only used for the request.',
    sendFailed: 'Failed to send request.',
    resultsTitle: 'Request results',
    // Results screen after sending the bulk request.
    results: {
      allSuccess: {
        one: 'All {count} request sent successfully',
        other: 'All {count} requests sent successfully',
      },
      partial: '{successCount} of {total} sent successfully',
      failed: {
        one: '{count} request failed',
        other: '{count} requests failed',
      },
      unknownIdentifier: 'Unknown',
    },
  },

  // niup-request-representation-popup.
  requestRepresentation: {
    title: 'Request representation',
    description: 'Ask for someone you know to be able to represent them. Send them a message by adding their email or Niup code.',
    contactLabel: 'Email or Niup code *',
    inputPlaceholder: 'e.g. name@email.com or NIUP-CODE-XXXX',
    hint: 'Press Enter or Tab to add multiple people',
    removeRecipient: 'Remove {name}',
    messageLabel: 'Personalised message',
    messagePlaceholder: 'Hello! Can you accept my representation request?',
    minCharacters: 'Minimum {count} characters',
    note: '* Required fields. The email will only be used for this invitation.',
    sendButton: 'Send request',
    sendFailed: 'Failed to send request. Please try again.',
  },

  // niup-association-edit-page.
  associationEdit: {
    breadcrumbCompanies: 'Companies',
    companyFallback: 'Company',
    typeOfUserDesc: "Select this user's type",
    rolesDesc: 'Select the role or roles assigned to the user',
    offlineDaysDesc: 'Define the number of days that this person can continue to login by the company even without network validation.',
  },

  // niup-association-view-page (+ helper).
  associationView: {
    breadcrumbPeoples: 'Peoples',
    breadcrumbCompanies: 'Companies',
    loginAliasTitle: 'Login Alias',
    aliasTitle: 'Alias',
    aliasDesc: 'Custom name for this association',
    aliasPlaceholder: 'Enter an alias',
    noRoles: 'No roles assigned',
    representedSince: '{name} represented since {date}',
    removeButton: 'Remove association',
  },
};
